import { Circle, Polygon } from '@react-google-maps/api';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import type { MarkerClusterer } from '@googlemaps/markerclusterer';

/**
 * Provide a MarkerClusterer instance via this context to have useAdvancedMarker
 * register markers with the clusterer instead of directly on the map.
 */
export const MarkerClustererContext = React.createContext<MarkerClusterer | null>(null);

/**
 * Module-level debounce map: batches addMarker(noDraw=true) calls so that a
 * single clusterer.render() fires after all markers in a React commit are added.
 */
const _pendingClustererRenders = new WeakMap<object, ReturnType<typeof setTimeout>>();

function scheduleClustererRender(clusterer: MarkerClusterer): void {
  const existing = _pendingClustererRenders.get(clusterer);
  if (existing !== undefined) {
    clearTimeout(existing);
  }
  _pendingClustererRenders.set(
    clusterer,
    setTimeout(() => {
      _pendingClustererRenders.delete(clusterer);
      try {
        clusterer.render();
      } catch (_) {
        /* ignore */
      }
    }, 0),
  );
}

const _clustererMarkers = new WeakMap<MarkerClusterer, Set<any>>();

function getClustererMarkerSet(clusterer: MarkerClusterer): Set<any> {
  let set = _clustererMarkers.get(clusterer);
  if (!set) {
    set = new Set();
    _clustererMarkers.set(clusterer, set);
  }
  return set;
}

function addMarkerToClusterer(clusterer: MarkerClusterer, marker: any, noDraw: boolean): void {
  if (getClustererMarkerSet(clusterer).has(marker)) {
    return;
  }
  clusterer.addMarker(marker, noDraw);
  getClustererMarkerSet(clusterer).add(marker);
}

function removeMarkerFromClusterer(clusterer: MarkerClusterer, marker: any, noDraw: boolean): void {
  try {
    clusterer.removeMarker(marker, noDraw);
  } catch (_) {
    /* ignore */
  }
  getClustererMarkerSet(clusterer).delete(marker);
}

/**
 * Attach a marker to `map` (or detach with null), covering both marker flavors.
 * AdvancedMarkerElement assigns `map`; legacy google.maps.Marker uses setMap().
 */
function attachMarker(marker: any, map: google.maps.Map | null): void {
  if (!marker) {
    return;
  }
  if (marker.map !== undefined) {
    // Always assign — do not gate on a truthy map. Orphans can already be null
    // but still need a definitive detach.
    marker.map = map;
  } else if (marker.setMap) {
    marker.setMap(map);
  }
}

function detachMarker(marker: any): void {
  attachMarker(marker, null);
}

export enum DrawingMode {
  NONE = 'NONE',
  MARKER = 'MARKER',
  CIRCLE = 'CIRCLE',
  POLYGON = 'POLYGON',
}

interface DrawingControlsProps {
  onModeChange: (mode: DrawingMode) => void;
  currentMode: DrawingMode;
  disabled?: boolean;
  position?: 'horizontal' | 'vertical';
}

export const DrawingControls: React.FC<DrawingControlsProps> = ({
  onModeChange,
  currentMode,
  disabled = false,
  position = 'horizontal',
}) => {
  const controlStyle: React.CSSProperties = {
    backgroundColor: 'white',
    border: '2px solid #ccc',
    borderRadius: '3px',
    boxShadow: '0 2px 6px rgba(0,0,0,.3)',
    cursor: 'pointer',
    marginTop: '8px',
    marginRight: '8px',
    display: 'flex',
    flexDirection: position === 'vertical' ? 'column' : 'row',
  };

  const buttonStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '8px',
    border: 'none',
    backgroundColor: isActive ? '#4285F4' : 'transparent',
    color: isActive ? 'white' : '#333',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    fontFamily: 'Roboto, Arial, sans-serif',
    opacity: disabled ? 0.6 : 1,
  });

  const handleClick = (mode: DrawingMode) => {
    if (!disabled) onModeChange(mode);
  };

  return (
    <div style={controlStyle}>
      <button
        style={buttonStyle(currentMode === DrawingMode.MARKER)}
        onClick={() => handleClick(DrawingMode.MARKER)}
        disabled={disabled}
        title="Add marker"
        type="button"
      >
        📍
      </button>
      <button
        style={buttonStyle(currentMode === DrawingMode.CIRCLE)}
        onClick={() => handleClick(DrawingMode.CIRCLE)}
        disabled={disabled}
        title="Draw circle"
        type="button"
      >
        ⭕
      </button>
      <button
        style={buttonStyle(currentMode === DrawingMode.POLYGON)}
        onClick={() => handleClick(DrawingMode.POLYGON)}
        disabled={disabled}
        title="Draw polygon"
        type="button"
      >
        ⬟
      </button>
      <button
        style={buttonStyle(currentMode === DrawingMode.NONE)}
        onClick={() => handleClick(DrawingMode.NONE)}
        title="Stop drawing"
        type="button"
      >
        ❌
      </button>
    </div>
  );
};

interface UseTTDrawingManagerProps {
  map?: google.maps.Map;
  onMarkerComplete?: (position: google.maps.LatLngLiteral) => void;
  onCircleComplete?: (circle: { center: google.maps.LatLngLiteral; radius: number }) => void;
  onPolygonComplete?: (polygon: { points: google.maps.LatLngLiteral[] }) => void;
  isEditable?: boolean;
}

export const useTTDrawingManager = ({
  map,
  onMarkerComplete,
  onCircleComplete,
  onPolygonComplete,
  isEditable = false,
}: UseTTDrawingManagerProps) => {
  const [drawingMode, setDrawingMode] = useState<DrawingMode>(DrawingMode.NONE);
  const [isDrawing, setIsDrawing] = useState(false);
  const [polygonPath, setPolygonPath] = useState<google.maps.LatLngLiteral[]>([]);
  const [circleCenter, setCircleCenter] = useState<google.maps.LatLngLiteral | null>(null);
  const circleCenterRef = useRef<google.maps.LatLngLiteral | null>(null);
  const [currentRadius, setCurrentRadius] = useState<number>(0);
  const [mousePosition, setMousePosition] = useState<google.maps.LatLngLiteral | null>(null);

  const drawingListeners = useRef<google.maps.MapsEventListener[]>([]);
  const mouseMoveListener = useRef<google.maps.MapsEventListener | null>(null);
  const polygonMouseMoveListener = useRef<google.maps.MapsEventListener | null>(null);

  const cleanupListeners = useCallback(() => {
    drawingListeners.current.forEach(l => google.maps.event.removeListener(l));
    drawingListeners.current = [];
    if (mouseMoveListener.current) {
      google.maps.event.removeListener(mouseMoveListener.current);
      mouseMoveListener.current = null;
    }
    if (polygonMouseMoveListener.current) {
      google.maps.event.removeListener(polygonMouseMoveListener.current);
      polygonMouseMoveListener.current = null;
    }
  }, []);

  const handleMarkerDraw = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (drawingMode !== DrawingMode.MARKER || !event.latLng) return;
      onMarkerComplete?.({ lat: event.latLng.lat(), lng: event.latLng.lng() });
      setDrawingMode(DrawingMode.NONE);
    },
    [drawingMode, onMarkerComplete],
  );

  const handleCircleDraw = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (drawingMode !== DrawingMode.CIRCLE || !event.latLng || !map) return;

      try {
        const clickPos = { lat: event.latLng.lat(), lng: event.latLng.lng() };
        if (!isDrawing) {
          setCircleCenter(clickPos);
          circleCenterRef.current = clickPos;
          setIsDrawing(true);
          setCurrentRadius(0);
          mouseMoveListener.current = map.addListener(
            'mousemove',
            (moveEvent: google.maps.MapMouseEvent) => {
              const center = circleCenterRef.current;
              if (moveEvent.latLng && center) {
                const dist = google.maps.geometry.spherical.computeDistanceBetween(
                  new google.maps.LatLng(center.lat, center.lng),
                  moveEvent.latLng,
                );
                setCurrentRadius(dist);
              }
            },
          );
        } else {
          const center = circleCenterRef.current;
          if (center) {
            const dist = google.maps.geometry.spherical.computeDistanceBetween(
              new google.maps.LatLng(center.lat, center.lng),
              event.latLng,
            );
            onCircleComplete?.({ center, radius: dist });
          }
          setIsDrawing(false);
          setCircleCenter(null);
          circleCenterRef.current = null;
          setCurrentRadius(0);
          if (mouseMoveListener.current) {
            google.maps.event.removeListener(mouseMoveListener.current);
            mouseMoveListener.current = null;
          }
          setDrawingMode(DrawingMode.NONE);
        }
      } catch (error) {
        setIsDrawing(false);
        setCircleCenter(null);
        circleCenterRef.current = null;
        setCurrentRadius(0);
        if (mouseMoveListener.current) {
          google.maps.event.removeListener(mouseMoveListener.current);
          mouseMoveListener.current = null;
        }
      }
    },
    [drawingMode, isDrawing, map, onCircleComplete],
  );

  const handlePolygonDraw = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (drawingMode !== DrawingMode.POLYGON || !event.latLng) return;
      setPolygonPath(path => [...path, { lat: event.latLng!.lat(), lng: event.latLng!.lng() }]);
    },
    [drawingMode],
  );

  const handlePolygonComplete = useCallback(
    (event?: google.maps.MapMouseEvent) => {
      if (drawingMode === DrawingMode.POLYGON) {
        if (polygonPath.length >= 3) {
          onPolygonComplete?.({ points: polygonPath });
          setPolygonPath([]);
          setDrawingMode(DrawingMode.NONE);
          setMousePosition(null);
        } else {
          setPolygonPath([]);
          setDrawingMode(DrawingMode.NONE);
          setMousePosition(null);
        }
      }
    },
    [drawingMode, polygonPath, onPolygonComplete],
  );

  // Track mouse position for polygon preview
  useEffect(() => {
    if (!map || drawingMode !== DrawingMode.POLYGON || polygonPath.length === 0) {
      setMousePosition(null);
      return undefined;
    }

    polygonMouseMoveListener.current = map.addListener(
      'mousemove',
      (e: google.maps.MapMouseEvent) => {
        if (e.latLng) {
          setMousePosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        }
      },
    );

    return () => {
      if (polygonMouseMoveListener.current) {
        google.maps.event.removeListener(polygonMouseMoveListener.current);
        polygonMouseMoveListener.current = null;
      }
      setMousePosition(null);
    };
  }, [map, drawingMode, polygonPath.length]);

  useEffect(() => {
    if (!map || !isEditable) return;
    cleanupListeners();
    map.setOptions({
      draggableCursor: drawingMode !== DrawingMode.NONE ? 'crosshair' : null,
      disableDoubleClickZoom: drawingMode !== DrawingMode.NONE,
    });
    if (drawingMode !== DrawingMode.NONE) {
      const clickL = map.addListener('click', (e: google.maps.MapMouseEvent) => {
        switch (drawingMode) {
          case DrawingMode.MARKER:
            handleMarkerDraw(e);
            break;
          case DrawingMode.CIRCLE:
            handleCircleDraw(e);
            break;
          case DrawingMode.POLYGON:
            handlePolygonDraw(e);
            break;
          default:
            break;
        }
      });

      const rightClickL = map.addListener('rightclick', (e: google.maps.MapMouseEvent) => {
        if (drawingMode === DrawingMode.POLYGON) {
          e.stop();
          handlePolygonComplete();
        }
      });

      drawingListeners.current.push(clickL, rightClickL);
    }
  }, [
    map,
    drawingMode,
    isEditable,
    handleMarkerDraw,
    handleCircleDraw,
    handlePolygonDraw,
    handlePolygonComplete,
    cleanupListeners,
  ]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cleanupListeners();
        setPolygonPath([]);
        setIsDrawing(false);
        setCircleCenter(null);
        circleCenterRef.current = null;
        setCurrentRadius(0);
        setDrawingMode(DrawingMode.NONE);
        setMousePosition(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cleanupListeners]);

  const previewCircle = useMemo(
    () =>
      circleCenter && isDrawing ? (
        <Circle
          center={circleCenter}
          radius={currentRadius}
          options={{
            fillOpacity: 0.1,
            strokeWeight: 2,
            strokeOpacity: 0.8,
            strokeColor: '#4285F4',
            fillColor: '#4285F4',
            clickable: false,
            zIndex: 1000,
          }}
        />
      ) : null,
    [circleCenter, isDrawing, currentRadius],
  );

  const previewPolygon = useMemo(() => {
    if (polygonPath.length === 0) return null;

    const path =
      mousePosition && polygonPath.length > 0 ? [...polygonPath, mousePosition] : polygonPath;

    return (
      <Polygon
        path={path}
        options={{
          fillOpacity: 0.1,
          strokeWeight: 2,
          strokeOpacity: 0.8,
          strokeColor: '#4285F4',
          fillColor: '#4285F4',
          clickable: false,
          zIndex: 1000,
        }}
      />
    );
  }, [polygonPath, mousePosition]);

  return {
    drawingMode,
    setDrawingMode,
    isDrawing,
    polygonPath,
    circleCenter,
    currentRadius,
    previewCircle,
    previewPolygon,
  };
};

interface TTDrawingManagerProps {
  map?: google.maps.Map;
  onMarkerComplete?: (pos: google.maps.LatLngLiteral) => void;
  onCircleComplete?: (circle: { center: google.maps.LatLngLiteral; radius: number }) => void;
  onPolygonComplete?: (polygon: { points: google.maps.LatLngLiteral[] }) => void;
  isEditable?: boolean;
  showControls?: boolean;
  controlPosition?: google.maps.ControlPosition;
  controlOrientation?: 'horizontal' | 'vertical';
}

export const CustomDrawingManager: React.FC<TTDrawingManagerProps> = ({
  map,
  onMarkerComplete,
  onCircleComplete,
  onPolygonComplete,
  isEditable = false,
  showControls = true,
  controlPosition = google.maps.ControlPosition.TOP_RIGHT,
  controlOrientation = 'horizontal',
}) => {
  const { drawingMode, setDrawingMode, previewCircle, previewPolygon } = useTTDrawingManager({
    map,
    onMarkerComplete,
    onCircleComplete,
    onPolygonComplete,
    isEditable,
  });

  useEffect(() => {
    if (!map || !showControls || !isEditable) return undefined;
    const controlDiv = document.createElement('div');
    map.controls[controlPosition].push(controlDiv);
    const root = createRoot(controlDiv);
    root.render(
      <DrawingControls
        currentMode={drawingMode}
        onModeChange={setDrawingMode}
        disabled={!isEditable}
        position={controlOrientation}
      />,
    );
    return () => {
      root.unmount();
      const idx = map.controls[controlPosition].getArray().indexOf(controlDiv);
      if (idx > -1) map.controls[controlPosition].removeAt(idx);
    };
  }, [
    map,
    showControls,
    isEditable,
    controlPosition,
    controlOrientation,
    drawingMode,
    setDrawingMode,
  ]);

  return (
    <>
      {previewCircle}
      {previewPolygon}
    </>
  );
};

// Hook for using AdvancedMarkerElement with fallback to regular Marker
interface UseAdvancedMarkerProps {
  map?: google.maps.Map;
  position: google.maps.LatLngLiteral;
  content?: HTMLElement;
  draggable?: boolean;
  visible?: boolean;
  zIndex?: number;
  onDragEnd?: (position: google.maps.LatLngLiteral) => void;
}

export const useAdvancedMarker = ({
  map,
  position,
  content,
  draggable = false,
  visible = true,
  zIndex,
  onDragEnd,
}: UseAdvancedMarkerProps) => {
  const [marker, setMarker] = useState<any>(null);
  const markerRef = useRef<any>(null);
  const dragListenerRef = useRef<any>(null);
  const clusterer = useContext(MarkerClustererContext);

  // Create marker once when map becomes available. Avoid including props that change
  // frequently in the dependency array to prevent repeated creations.
  useEffect(() => {
    // When a clusterer is provided it owns the map assignment; map is optional.
    if (!map && !clusterer) return undefined;

    let newMarker: any;

    try {
      const g: any = (window as any).google;
      const AdvancedMarkerCtor: any =
        g?.maps?.marker?.AdvancedMarkerElement || g?.maps?.AdvancedMarkerElement || null;

      if (AdvancedMarkerCtor) {
        newMarker = new AdvancedMarkerCtor({
          map: null,
          position,
          content,
          gmpDraggable: draggable,
          zIndex,
          gmpClickable: true,
        });
      } else {
        // Build icon synchronously based on content
        let icon: google.maps.Icon | undefined;
        if (content) {
          const svgEl =
            (content as any).nodeName === 'svg'
              ? (content as unknown as SVGElement)
              : content.querySelector && content.querySelector('svg');
          const imgEl =
            !svgEl && content.querySelector
              ? (content.querySelector('img') as HTMLImageElement | null)
              : null;

          if (svgEl) {
            const svgString = (svgEl as SVGElement).outerHTML;
            const dataUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgString)}`;
            // Using svgEl to create marker icon dataUrl (initial create)
            icon = { url: dataUrl, scaledSize: new google.maps.Size(40, 40) } as google.maps.Icon;
          } else if (imgEl && imgEl.src) {
            icon = {
              url: imgEl.src,
              scaledSize: new google.maps.Size(imgEl.width || 40, imgEl.height || 40),
            } as google.maps.Icon;
          } else {
            icon = {
              url: 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=',
              scaledSize: new google.maps.Size(1, 1),
            } as google.maps.Icon;
          }
        } else {
          icon = {
            url: 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=',
            scaledSize: new google.maps.Size(1, 1),
          } as google.maps.Icon;
        }

        // Creating google.maps.Marker (initial create)
        // When a clusterer is present, leave map as null — the clusterer owns map
        // assignment. Attaching directly here would cause duplicate rendering.
        newMarker = new google.maps.Marker({
          map: clusterer ? null : visible ? map : null,
          position,
          draggable,
          zIndex,
          icon,
          optimized: false,
        });
      }

      markerRef.current = newMarker;
      setMarker(newMarker);
    } catch (error) {
      // Error creating marker - intentionally silent in production builds
    }

    return () => {
      const marker = markerRef.current || newMarker;
      if (marker) {
        if (clusterer) {
          removeMarkerFromClusterer(clusterer, marker, true);
          detachMarker(marker);
          scheduleClustererRender(clusterer);
        } else {
          detachMarker(marker);
        }
      }
      markerRef.current = null;
      setMarker(null);
    };
    // Intentionally omit content/position/draggable/zIndex/visible/onDragEnd from
    // deps to avoid re-creating the marker on prop changes. Visibility is managed
    // by the dedicated effect below; all others are updated in their own effects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, clusterer]);

  // Update marker icon/content/zIndex when those props change without recreating the marker
  useEffect(() => {
    if (!marker) return undefined;

    try {
      const isAdvanced = !!marker.position; // AdvancedMarkerElement exposes position property

      if (isAdvanced) {
        // AdvancedMarkerElement supports direct content assignment. Skip
        // reassigning the same element (memoized upstream, DEV-4500) so a
        // zIndex-only change doesn't detach/reattach the marker's DOM.
        if (content !== undefined && marker.content !== content) marker.content = content;
        if (zIndex !== undefined) marker.zIndex = zIndex;
      } else {
        // Regular google.maps.Marker: build icon like in creation
        let icon: google.maps.Icon | undefined;
        if (content) {
          const svgEl =
            (content as any).nodeName === 'svg'
              ? (content as unknown as SVGElement)
              : content.querySelector && content.querySelector('svg');
          const imgEl =
            !svgEl && content.querySelector
              ? (content.querySelector('img') as HTMLImageElement | null)
              : null;

          if (svgEl) {
            const svgString = (svgEl as SVGElement).outerHTML;
            const dataUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgString)}`;
            // Updating marker icon from svgEl
            icon = { url: dataUrl, scaledSize: new google.maps.Size(40, 40) } as google.maps.Icon;
          } else if (imgEl && imgEl.src) {
            icon = {
              url: imgEl.src,
              scaledSize: new google.maps.Size(imgEl.width || 40, imgEl.height || 40),
            } as google.maps.Icon;
          } else {
            icon = {
              url: 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=',
              scaledSize: new google.maps.Size(1, 1),
            } as google.maps.Icon;
          }
        } else {
          icon = {
            url: 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=',
            scaledSize: new google.maps.Size(1, 1),
          } as google.maps.Icon;
        }

        if (marker.setIcon) marker.setIcon(icon);
        if (zIndex !== undefined) {
          if (marker.setZIndex) marker.setZIndex(zIndex as number);
          else marker.zIndex = zIndex;
        }
      }
    } catch (e) {
      // Error updating marker content/icon/zIndex - intentionally silent
    }
    return undefined;
  }, [marker, content, zIndex]);

  // Manage draggable and dragend listener without recreating marker
  useEffect(() => {
    if (!marker) return;

    // remove existing listener if present
    if (dragListenerRef.current) {
      try {
        google.maps.event.removeListener(dragListenerRef.current);
      } catch (_) {
        // ignore
      }
      dragListenerRef.current = null;
    }

    try {
      if (marker.setDraggable) {
        marker.setDraggable(!!draggable);
      } else if ('gmpDraggable' in marker) {
        marker.gmpDraggable = !!draggable;
      }

      if (draggable && onDragEnd) {
        const handler = () => {
          try {
            if (marker.position) {
              const pos = marker.position;
              const lat = typeof pos.lat === 'function' ? pos.lat() : pos.lat;
              const lng = typeof pos.lng === 'function' ? pos.lng() : pos.lng;
              onDragEnd({ lat, lng });
            } else if (marker.getPosition) {
              const p = marker.getPosition();
              if (p) onDragEnd({ lat: p.lat(), lng: p.lng() });
            }
          } catch (err) {
            // Error in dragend handler - intentionally silent
          }
        };

        if (marker.addListener) {
          dragListenerRef.current = marker.addListener('dragend', handler);
        }
      }
    } catch (e) {
      // Error setting draggable/drag listener - intentionally silent
    }
    // No cleanup needed for this effect
  }, [marker, draggable, onDragEnd]);

  // Update position when it changes
  useEffect(() => {
    if (!marker) return;

    if (marker.position) {
      // AdvancedMarkerElement
      marker.position = position;
    } else if (marker.setPosition) {
      // Regular Marker
      marker.setPosition(position);
    }
  }, [marker, position]);

  // Owns attachment: the AdvancedMarkerElement branch above always constructs with
  // `map: null`, so this effect is what puts a marker on the map.
  // Deps are the values the body actually reads. `marker` is deliberately not one
  // of them — it is state, and on a `map` identity change the create-effect
  // cleanup + recreate run in this same commit flush, leaving state one marker
  // behind. Attaching the stale marker would re-bind an orphaned AdvancedMarker
  // onto the new map (stacked/opaque overlays), so read markerRef.current, which
  // the create effect has already repointed.
  useEffect(() => {
    const currentMarker = markerRef.current;
    if (!currentMarker) return;

    if (clusterer) {
      if (visible) {
        addMarkerToClusterer(clusterer, currentMarker, true);
      } else {
        removeMarkerFromClusterer(clusterer, currentMarker, true);
        detachMarker(currentMarker);
      }
      scheduleClustererRender(clusterer);
    } else {
      attachMarker(currentMarker, visible ? (map ?? null) : null);
    }
  }, [map, visible, clusterer]);

  return marker;
};
